// Pure helpers of the networks list, New network and the network tabs (feature spec 17, phase 1).
import type { KeyboardEvent } from 'react';
import type { AttachmentCounts, Diagnostic, EdgeKind, KindStatus } from '../../api/types';
import { strings } from '../../strings';
import { providerLabel } from '../shared';

const l = strings.networks.lists;

/** The reason badge of a kind that isn't available (spec 11 6.15.10): its words, never color alone. */
export function kindReasonLabel(status: KindStatus, provider: string | null, network: string | null): string {
  const e = l.entryPoints;
  if (status === 'disabled') return e.disabled;
  if (status === 'needs-provider' && provider && network)
    return e.needsProvider(providerLabel(provider), network);
  return e.comingLater;
}

/** "4 connected · 1 connecting · 1 pending", zero counts left out, "none yet" when all are zero (6.15.2). */
export function attachmentWords(counts: AttachmentCounts): string {
  const c = l.list.count;
  const parts = [
    counts.connected ? c.connected(counts.connected) : null,
    counts.connecting ? c.connecting(counts.connecting) : null,
    counts.pending ? c.pending(counts.pending) : null,
  ].filter((p): p is string => p !== null);
  const any = parts.length || counts.waitingForApply || counts.failed || counts.retained;
  return parts.length ? parts.join(' · ') : any ? '' : l.list.noneYet;
}

/**
 * Roving focus of a radiogroup (one Tab stop; arrows move and choose, Home/End, Space chooses). `ids` are the radios'
 * element IDs in order; `choose` is called for the radio moved to (it ignores unavailable ones).
 */
export function rovingKeyDown(
  event: KeyboardEvent<HTMLElement>,
  ids: readonly string[],
  index: number,
  choose: (index: number) => void,
) {
  let next: number | null = null;
  if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (index + 1) % ids.length;
  else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (index - 1 + ids.length) % ids.length;
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = ids.length - 1;
  else if (event.key === ' ' || event.key === 'Enter') {
    event.preventDefault();
    choose(index);
    return;
  }
  if (next === null) return;
  event.preventDefault();
  document.getElementById(ids[next])?.focus();
  choose(next);
}

/** New network's step of a diagnostic (spec 11 6.15.3); null: the list of the step being left. */
export function stepOfDiagnostic(d: Diagnostic): number | null {
  const path = d.path ?? '';
  if (path === '/name' || path.startsWith('/settings/description')) return 1;
  if (/^\/settings\/providers\/[^/]+\/allowedAccounts/.test(path)) return 5;
  if (path.startsWith('/settings/attachPolicy') || d.code === 'NETWORK_ACCOUNT_OTHER_TENANT') return 5;
  if (
    path.startsWith('/cloudAccounts') ||
    path.startsWith('/settings/providers') ||
    d.code === 'NETWORK_PROVIDER_DISABLED' ||
    d.code === 'NETWORK_ACCOUNT_MISSING'
  )
    return 2;
  if (path.startsWith('/settings/addressPlan') || d.code.startsWith('IPAM_')) return 3;
  if (path.startsWith('/settings/topology')) return 4;
  return null;
}

/** Whether a kind matches the search: its label, abbreviation, add-on summary and add-on labels (6.15.10). */
export function kindMatches(kind: EdgeKind, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [kind.label, kind.abbr, kind.addOnSummary ?? '', ...kind.addOns.map((a) => a.label)].some((text) =>
    text.toLowerCase().includes(q),
  );
}
